import { DateRangePicker } from '@modules/common/components/DateRangePicker/DateRangePicker';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import {
  formatDateTimeRange,
  getAggregatePeriodFromDateRange,
} from '@modules/common/utils';
import { CompareHighlightWrapper } from '@modules/quantify/components/CompareHighlightWrapper';
import { DEFAULT_QUANTIFY_SORT_BY } from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useIsDurationCustomizationPossible,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { ScheduleRounded } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

export function QuantifyDurationField() {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const query = useQuantifyStore(({ query }) => query);
  const shouldHighlightDurationField = useQuantifyStore(
    ({ visual }) => visual.shouldHighlightDurationField,
  );
  const isTableQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isTableQueryInProgress,
  );
  const isChartQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isChartQueryInProgress,
  );
  const isDurationCustomizationPossible = useIsDurationCustomizationPossible();
  const quantifyQueryAnalyticsEvent = useTriggerQuantifyQueryAnalyticsEvent();
  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const isValidQueryState = isValidQuery(query);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
      quantifyQueryAnalyticsEvent('Quantify - Query Executed');
    }
  }, [
    fetchReportData,
    isValidQueryState,
    quantifyQueryAnalyticsEvent,
    shouldFetchReportData,
  ]);

  function onDurationchange(
    newDuration: DateTimeRange,
    isQuickLookupOption?: boolean,
  ) {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        duration: newDuration,
        sortBy: DEFAULT_QUANTIFY_SORT_BY,
        shouldShowTopDataPoints: true,
        visibleDataPoints: [],
      },
    });

    if (isQuickLookupOption) {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          aggregatePeriod: getAggregatePeriodFromDateRange(newDuration),
        },
      });
    }

    setShouldFetchReportData(true);
    quantifyQueryAnalyticsEvent('Quantify - Duration Selected', {
      duration: formatDateTimeRange(newDuration),
    });
  }

  const quickOptionsFilters = useMemo(() => ({ skipYesterday: true }), []);

  const isReadOnly =
    !query.groupByField ||
    !isDurationCustomizationPossible ||
    isTableQueryInProgress ||
    isChartQueryInProgress;

  return (
    <CompareHighlightWrapper isActive={shouldHighlightDurationField}>
      <Stack spacing={1} px={2} py={1.5} data-testid="quantify-duration-field">
        <Stack spacing={1} direction="row" alignItems="center">
          <ScheduleRounded
            sx={{
              color: 'text.secondary',
            }}
          />
          <Typography variant="subtitle1">Duration</Typography>
        </Stack>
        <Tooltip
          placement="top"
          title={
            isDurationCustomizationPossible
              ? undefined
              : 'Duration customizations are not applicable to queries which do no contain any Feedback Fields'
          }
        >
          <Box pl={4} sx={{ opacity: isReadOnly ? 0.5 : 1 }}>
            <DateRangePicker
              timeRange={query.duration}
              onChange={onDurationchange}
              isReadOnly={isReadOnly}
              shouldShowQuickLookupOptions
              quickOptionsFilters={quickOptionsFilters}
            />
          </Box>
        </Tooltip>
      </Stack>
    </CompareHighlightWrapper>
  );
}
